import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';
import { useTranslations } from "@/hooks/useTranslations";
import { Message } from '../types';

export const useMessages = (channelId?: string, recipientId?: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useTranslations('messages');

  const fetchMessages = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      
      if (!channelId && !recipientId) {
        setMessages([]);
        setLoading(false);
        return;
      }
      
      let query = supabase.from('messages').select('*');
      
      if (channelId) {
        query = query.eq('channel_id', channelId);
      } else if (recipientId) {
        query = query.or(`and(sender_id.eq.${user.id},recipient_id.eq.${recipientId}),and(sender_id.eq.${recipientId},recipient_id.eq.${user.id})`);
      }
      
      query = query.order('created_at', { ascending: true });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching messages:', error);
        toast.error(t('errorLoading') || 'Erro ao carregar mensagens.');
        setMessages([]);
        return;
      }
      
      const senderIds = [...new Set((data || []).map(msg => msg.sender_id))];
      let profiles: any[] = [];
      if (senderIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', senderIds);
        profiles = profilesData || [];
      }
          
      const processedMessages: Message[] = (data || []).map(msg => {
        const senderProfile = profiles.find(p => p.id === msg.sender_id);
        const senderName = senderProfile 
          ? `${senderProfile.first_name || ''} ${senderProfile.last_name || ''}`.trim()
          : t('unknownUser') || 'Utilizador Desconhecido';
          
        return {
          ...msg,
          sender_name: senderName
        } as Message;
      });
      
      setMessages(processedMessages);
    } catch (error) {
      console.error('Error in fetchMessages:', error);
      toast.error(t('errorLoading') || 'Erro ao carregar mensagens.');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, channelId, recipientId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);
    
  useEffect(() => {
    if (!user?.id) return;
    
    const handleNewMessage = async (payload: any) => {
      const newMessageData = payload.new;

      const isChannelMessage = channelId && newMessageData.channel_id === channelId;
      const isDirectMessage = recipientId && 
        ((newMessageData.sender_id === user.id && newMessageData.recipient_id === recipientId) || 
         (newMessageData.sender_id === recipientId && newMessageData.recipient_id === user.id));

      if (isChannelMessage || isDirectMessage) {
        const { data: senderData } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', newMessageData.sender_id)
          .single();
            
        const senderName = senderData
          ? `${senderData.first_name || ''} ${senderData.last_name || ''}`.trim()
          : t('unknownUser') || 'Utilizador Desconhecido';
            
        const newMessage: Message = {
          ...newMessageData,
          sender_name: senderName
        } as Message;
        
        setMessages(prev => [...prev, newMessage]);
      }
    };

    const subscriptionChannel = supabase
      .channel(`messages_for_${user.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' }, 
        handleNewMessage
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(subscriptionChannel);
    };
  }, [user?.id, channelId, recipientId, t]);

  return { messages, loading };
};
